




































































































































import { Component, Vue, Prop, Watch, Ref } from 'nuxt-property-decorator';
import { Editor, EditorContent } from '@tiptap/vue-2';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import SuperScript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import StarterKit from '@tiptap/starter-kit';
import CloseBtn from '../CloseBtn.vue';
import TooltipButton from '@/components/ui/TooltipButton.vue';

export type VForm = Vue & {
  validate: () => boolean;
  resetValidation: () => boolean;
  reset: () => void;
};

@Component({
  components: {
    CloseBtn,
    EditorContent,
    TooltipButton
  }
})
export default class MarkdownEditor extends Vue {
  @Prop({ type: String, default: '' })
  value: string;

  @Prop({ type: Boolean, default: true })
  editable!: boolean;

  @Prop({ type: Number, default: 0 })
  characterLimit!: number;

  @Prop({ type: String, default: '' })
  placeholder: string;

  editor: Editor = null;
  showLinkPrompt: boolean = false;
  isFormValid: boolean = false;
  linkRules = [
    (str) =>
      MarkdownEditor.isUrl(str) ||
      (this.$t('labels.editor.must-be-link') as String)
  ];

  urlFromDialogue: string = '';

  get iconColor(): string {
    return this.$vuetify.theme.dark ? 'white--text' : 'black--text';
  }

  get reachedCharLimit(): boolean {
    return (
      this.editor &&
      this.editable &&
      this.editor.storage.characterCount.characters({}) === this.characterLimit
    );
  }

  @Ref()
  linkForm: VForm;

  @Watch('value')
  onValueChange(val: string) {
    const isSame = this.editor.getHTML() === val;

    if (isSame) {
      return;
    }

    this.editor.commands.setContent(val, false);
  }

  mounted() {
    this.editor = new Editor({
      editable: this.editable,
      autofocus: true,
      extensions: [
        StarterKit,
        CharacterCount.configure({ limit: this.characterLimit }),
        Placeholder.configure({ placeholder: '' }),
        Link.configure({
          HTMLAttributes: {
            // Global class used for external links
            class: 'external-link'
          },
          openOnClick: false,
          autolink: true,
          linkOnPaste: true
        }),
        SubScript.configure({}),
        SuperScript.configure({})
      ],
      content: this.value,
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML());
      }
    });
  }

  beforeDestroy() {
    this.editor.destroy();
  }

  addLink() {
    // @ts-ignore - setLink exists, comes from extension-link package.
    this.editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: this.urlFromDialogue })
      .run();
    this.closeLinkDialogue();
  }

  closeLinkDialogue() {
    this.showLinkPrompt = false;
    // Let's remember the last used link
    // this.urlFromDialogue = '';
  }

  get isSelectionValidForLink(): boolean {
    // https://prosemirror.net/docs/ref/#state.Selection
    const { empty, $from, $to } = this.editor.view.state.selection;
    return !empty || $to.pos - $from.pos > 0 || this.editor.isActive('link');
  }

  openLinkDialogue() {
    if (!this.showLinkPrompt) {
      if (this.editor.isActive('link')) {
        // unset link if we are hovering a link already there.
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        this.showLinkPrompt = true;
      }
    }
  }

  handleButtonState(editorBtnState: boolean): string {
    return editorBtnState ? 'v-btn--rte v-btn--rte--active' : 'v-btn--rte';
  }

  submitHandler(): void {
    if (this.linkForm.validate()) {
      this.addLink();
    }
  }

  static isUrl(str: string): boolean {
    let url;
    try {
      url = new URL(str);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  }
}
